/*
 * @Copyright 2023-2025. Institute for Future Intelligence, Inc.
 */

import type { ConservationRecord } from '../biomolecule/conservationService.ts';
import { createWithEqualityFn } from 'zustand/traditional';
import { produce } from 'immer';
import {
  ChatAttachment,
  ChatMessageInterface,
  LessonPlanRecord,
  Message,
  MoleculeInterface,
  MoleculeTransform,
} from '../types';
import { PickMode, ProjectType } from '../constants';
import { Molecule } from '../models/Molecule.ts';
import type { ScoreAtom, DockingScoreResult } from '../models/docking/DockingScore.ts';
import type { SearchPose } from '../models/docking/DockingSearch.ts';
import type { TorsionBond } from '../models/docking/LigandTorsions.ts';
import type { Pocket } from '../models/docking/detectPockets.ts';
import type { PharmacophoreFeature } from '../view/pharmacophore.ts';
import type { SceneSnapshot, SceneDelta } from '../agent/sceneSnapshot.ts';
import type { QuantTarget } from '../agent/investigationVerdict.ts';
import { PolynomialRegression } from 'ml-regression-polynomial';
import { useRefStore } from './commonRef.ts';
import type { CrystalAtomStateRequest } from '../crystal/crystalAtomStateRequest.ts';
import { readPersistedMuteUndoMessage } from '../muteUndoMessageStorage.ts';

// One agent-authored marker drawn on the IR-spectrum chart. The chat agent
// computes the spectrum (computeIRSpectrum), interprets the returned peaks,
// then calls the annotateSpectrum tool to label them here so the assignment
// shows up ON the graph (the render surface) rather than only as chat prose.
// `color` is optional — the panel falls back to a shared AI-accent color.
export interface SpectrumAnnotation {
  wavenumberCm1: number;
  label: string;
  color?: string;
}

// A generic agent-authored marker drawn on a line-chart analysis panel: a
// labeled vertical line at position `x` on that chart's X axis. The unit of
// `x` is the chart's own axis — Å for the g(r) panel (gOfRAnnotations), fs for
// the energy graph (energyAnnotations). Same "chat drives, graph shows"
// mechanism as SpectrumAnnotation, generalized across the other charts.
export interface GraphAnnotation {
  x: number;
  label: string;
  color?: string;
}

// A scientific-inquiry experiment the Molecular Modeling agent runs: a
// falsifiable prediction stated BEFORE the run (via the recordHypothesis tool),
// then the observed outcome + a confirm/refute verdict AFTER it (reportFinding).
// The `observable` is anything the simulation/analysis tools expose (energy
// drift, diffusion, a g(r) peak, Rg, a bond length, reactive-species counts…),
// not just a geometric measurement. Ephemeral (not persisted) — one active
// inquiry at a time, replaced when a new hypothesis is recorded.
//
// Verdicts are COMPUTED on the client from the numbers (agent/investigationVerdict.ts)
// whenever the prediction is machine-checkable: an absolute `target` on the
// observable, or a direction relative to a `baselineValue`. The model's own
// verdict is kept only when nothing checkable was supplied, and the card flags
// it. Fields added for that are optional so turns saved before they existed
// still render.
export interface AgentHypothesis {
  statement: string; // the falsifiable, quantitative prediction
  observable: string; // what will be measured/observed (any sim/analysis quantity)
  predictedDirection?: 'increase' | 'decrease' | 'no-change' | 'value';
  predictedValue?: number;
  unit?: string;
  independentVariable?: string; // what is being changed
  plan?: string; // brief experiment steps
  // Checkable absolute prediction (predictedDirection 'value'): observable must
  // satisfy this. Mirrors predictedValue + unit but carries the comparator.
  target?: QuantTarget;
  // Checkable size of a predicted increase / decrease (either or both); for
  // 'no-change' they are the tolerance inside which the observable counts as
  // unchanged.
  minChange?: number; // in the observable's unit
  minChangePercent?: number; // % of the baseline
}
// Where a reported number came from: the tool that produced it, whether that
// tool verifiably ran this turn (per the run's tool ledger), and the chart
// attachment it pushed, if any (index into the message's `attachments`).
export interface MeasurementEvidence {
  tool?: string;
  verified: boolean;
  attachmentIndex?: number;
}
export interface AgentFinding {
  verdict: 'confirmed' | 'refuted' | 'inconclusive';
  observedValue?: number;
  observedSummary?: string; // when the outcome isn't a single number
  reasoning: string; // why, comparing observed vs predicted
  baselineValue?: number; // the observable before the change, for directional predictions
  // True when the verdict was computed from the numbers; false/undefined when it
  // is the model's own claim (nothing checkable was available).
  computed?: boolean;
  computedDetail?: string; // one-line arithmetic behind a computed verdict
  // The model's verdict when it disagreed with the computed one (card shows it).
  reportedVerdict?: 'confirmed' | 'refuted' | 'inconclusive';
  evidence?: MeasurementEvidence;
}
export interface AgentInquiry {
  hypothesis: AgentHypothesis;
  finding: AgentFinding | null;
}

// An engineering-design challenge the agent runs (any project type): a goal with
// explicit success CRITERIA & constraints stated up front (recordDesignGoal),
// then a build → test → evaluate → iterate loop, and a met/not-met outcome with
// the final candidate and its measured properties (reportDesignOutcome). The
// parallel of AgentInquiry for CREATE-TO-SPEC work. Ephemeral (not persisted).
// Per-criterion pass/fail is COMPUTED from `spec` + the measured number when
// the criterion is machine-checkable; the model's `met` is kept only otherwise.
export interface AgentDesignCriterion {
  property: string; // e.g. "logP", "band gap", "Tg", "binding score"
  target: string; // display target, e.g. "< 3", "~1.5 eV", "≥ 100 °C"
  spec?: QuantTarget; // checkable form of `target`
}
export interface AgentDesignResult {
  property: string; // matches a criterion's property
  measured: string; // display measured value, e.g. "2.4"
  met: boolean; // whether this criterion is satisfied
  value?: number; // the measured number (what `met` was computed from)
  computed?: boolean; // true when `met` came from spec + value, not the model
  reportedMet?: boolean; // the model's claim when it disagreed with the computed one
  evidence?: MeasurementEvidence;
}
export interface AgentDesignGoal {
  objective: string; // one-line design goal
  criteria: AgentDesignCriterion[]; // the success spec
  constraints?: string; // hard constraints (e.g. "no heavy metals", "≤ 40 atoms")
}
export interface AgentDesignOutcome {
  verdict: 'met' | 'partially-met' | 'not-met';
  candidate: string; // the final design (molecule / crystal / polymer / ligand name)
  results: AgentDesignResult[]; // measured value + pass/fail per criterion
  iterations?: number; // how many build→test→evaluate cycles were run
  reasoning: string; // why this verdict (not shown on the card; the prose carries it)
  reportedVerdict?: 'met' | 'partially-met' | 'not-met'; // the model's claim when it disagreed
}
export interface AgentDesign {
  goal: AgentDesignGoal;
  outcome: AgentDesignOutcome | null;
}

// Module-level buffer for reasoning-stream coalescing. Lives outside the
// store because immer makes string concat O(n) in draft-tracking overhead,
// and we want to absorb dozens of chunks per frame into a single mutation.
// Reset by clearReasoningStream() so a fresh generation always starts clean.
let reasoningStreamPending = '';
let reasoningStreamFlushScheduled = false;

// Synchronous peek at the common store's persisted state — used to default
// `showLanding` to false when a returning user is being restored to a
// project. Keyed on the same 'aims-storage' name the common store's persist
// middleware writes (see stores/common.ts). Best-effort: any parse failure
// or missing key falls through to "no project found" so the landing shows
// for genuinely fresh visitors.
const hasRestoredProject = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem('aims-storage');
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return !!parsed?.state?.projectState?.title;
  } catch {
    return false;
  }
};

// If the URL carries ?template=..., the user came in via "Try this idea"
// on the case-studies page. Skip the landing entirely so the template
// loader doesn't have to fight a flash of the picker. Mirror's the
// runtime check in caseStudyTemplates.ts to keep them in sync.
const hasTemplateInUrl = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('template') !== null;
  } catch {
    return false;
  }
};

// Protein Lab ANM mode-animation payload (ephemeral UI state).
export interface BioModeAnimation {
  name: string; // structure the mode belongs to
  basePositions: [number, number, number][]; // Cα coordinates (structure frame)
  chains: string[]; // chain id per residue (for per-chain trace segments)
  modeVectors: [number, number, number][]; // per-residue 3-D displacement (max |v| = 1)
  amplitude: number; // peak displacement in Å
  source?: string; // who started it ('analysisPanel', a card, the agent) — only that owner tears it down
}

// One atom picked by the Protein Lab "inspect / measure" tool. Position is in the
// parsed Complex frame (same frame the rendered structure uses before the lab's
// bounding-sphere recenter), so overlays drawn inside the structure group align.
export interface BioPick {
  index: number; // global atom index in the Complex
  element: string; // element symbol, e.g. "C", "FE"
  atomName: string; // atom name, e.g. "CA", "FE"
  resName: string; // residue name, e.g. "HIS"
  resSeq: number; // residue sequence number
  iCode?: string; // insertion code ('' = none)
  chainId: string; // chain id
  pos: [number, number, number]; // Complex-frame position (Å)
}

// Which interaction families the Protein Lab overlay currently draws in 3-D.
export interface BioInteractionOverlay {
  disulfide: boolean;
  saltbridge: boolean;
  hbond: boolean;
}

// A residue identity shared by the structure-walkthrough tools: PDB chain id
// (trimmed, '' when blank) + residue number.
export interface BioResidueRef {
  chainId: string;
  resSeq: number;
  iCode?: string; // PDB insertion code ('' / absent = none): 184A is not 184
}

export type BioLigandDockOutcome =
  | { ok: true; converged: boolean; steps: number }
  // not_ready: no scorer / ligand atoms yet (structure still parsing);
  // search_failed / relax_failed: the orientation search or the relax threw;
  // interrupted: the 3-D view went away (or the tool gave up) mid-dock.
  | { ok: false; error: 'not_ready' | 'search_failed' | 'relax_failed' | 'interrupted' };

// Residue highlight driven by the agent's highlightResidues tool (or the chat
// Sequence card): the viewer splits the structure into a dimmed base rep and an
// accent-colored rep for `residues`. Gated on `name` matching the selected
// structure so a stale highlight never paints the wrong protein. Transient.
export interface BioHighlight {
  name: string; // structure the highlight belongs to
  residues: BioResidueRef[];
  color: string; // CSS hex, e.g. '#ff8c00'
  dim: boolean; // fade everything else
  label?: string; // caption drawn at the selection's centroid in 3-D
}

// Quaternary-structure "explode": the shown structure's chains are rendered as
// separate representations and each is translated by `offset` (Å, structure
// frame) so the subunits pull apart along their centroid vectors. Set by the
// agent's showAssembly tool; gated on `name`. Transient.
export interface BioExplode {
  name: string;
  separation: number; // Å each chain moved from the assembly centroid
  chains: { id: string; centroid: [number, number, number]; offset: [number, number, number] }[];
}

// One-shot "frame the camera on these residues" request. The viewer resolves
// the residues' atoms, moves the camera, and ignores a request whose `seq` it
// has already served — so a store subscriber never re-frames on an unrelated
// re-render.
export interface BioFocusRequest {
  seq: number;
  name: string;
  residues: BioResidueRef[];
}

// Tier-3 state comparison: a SECOND structure co-rendered, aligned onto the
// currently-loaded one. `matrix` is a column-major 16-element Matrix4 placing the
// mobile structure into the reference frame (from superposeStructures). Gated on
// `refName` matching the selected structure so a stale comparison never renders
// against the wrong reference.
export interface BioComparison {
  refName: string; // the reference (selected) structure the alignment was computed against
  name: string; // the mobile/comparison structure (must be in projectState.biomolecules)
  matrix: number[]; // 16-element Matrix4 (column-major) placing the mobile structure into the reference frame
  rmsd?: number; // Cα RMSD over the aligned region (Å), for the readout
  labels?: string[]; // per-aligned-residue "chain:resSeq" (parallel to deviation), for color-by-displacement
  deviation?: number[]; // per-aligned-residue Cα deviation (Å), drives color-by-displacement + top-movers
  // The mobile residue paired with each label (parallel): the morph trace moves
  // exactly the residues the fit paired. Absent → paired by order (legacy).
  movLabels?: string[];
}

// avoid using undefined value in the store for now.
export interface PrimitiveStoreState {
  latestVersion: string | undefined;
  changed: boolean;
  setChanged: (b: boolean) => void;
  // In-lab energy minimization (FIRE). `minimizeLabFlag` is a request the
  // simulation controls consume to start a relax; `minimizing` is true while it
  // runs (drives the "Minimizing energy…" overlay, distinct from the MD run
  // state); `minimizingEnergy` is the live potential energy (eV) shown dropping;
  // `cancelMinimizeFlag` is polled to stop early. All ephemeral (not persisted).
  minimizeLabFlag: boolean;
  minimizing: boolean;
  minimizingEnergy: number | null;
  cancelMinimizeFlag: boolean;
  skipChange: boolean;
  setSkipChange: (b: boolean) => void;
  muteUndoMessage: boolean;
  updateViewerFlag: boolean;
  updateViewer: () => void;
  updateInfoFlag: boolean;
  updateInfo: () => void;

  message: Message | undefined;

  dragAndDropMolecule: boolean;
  hoveredMolecule: MoleculeInterface | null;
  // Protein Lab ANM mode animation. Set by the analysis panel's "Animate" button
  // (which computes the ANM mode off-thread) and read by the lab viewer's
  // overlay, which oscillates the Cα trace along `modeVectors`. Null = not
  // animating. `name` ties it to the structure it was computed for so a stale
  // mode isn't drawn after the user switches structures. Ephemeral (not saved).
  bioModeAnimation: BioModeAnimation | null;
  // Protein Lab "color by predicted mobility". Holds a copy of the selected
  // structure's PDB with GNM flexibility written into the B-factor column; when
  // set (and `name`-matched), the viewer renders THIS instead of the original
  // and forces Temperature coloring, so the cartoon is painted by mobility.
  // Ephemeral (not saved).
  // A per-residue scalar painted onto a structure through the B-factor column
  // of `data` (a PDB text with the values written in): GNM mobility by default
  // (the chem-lib temperature gradient), or any other quantity when `gradient`
  // and `range` name the palette gradient and the value span — the Conservation
  // card uses the 'conservation' gradient over grades 1–9.
  // `source` names the card that painted it, so each card knows the coloring is its own.
  bioMobilityColoring: {
    name: string;
    data: string;
    gradient?: string;
    range?: [number, number];
    source?: string;
  } | null;
  // Evolutionary conservation per structure name (biomolecule/conservationService.ts):
  // the scores and the B-factor-painted PDB the viewer reads when the coloring is
  // "Conservation". Session cache — menu, settings panel, card and agent share it.
  bioConservation: Record<string, ConservationRecord>;
  // Cached gallery-card snapshots for the Protein / DNA-RNA labs, keyed by
  // structure name → downscaled JPEG data URL. Captured once from the live lab
  // canvas the first time a structure is rendered (bioStructureViewer), then
  // shown on its gallery card so you see the fold at a glance without a live
  // per-card 3D render. Ephemeral (not saved) — regenerated cheaply on next view.
  bioThumbnails: Record<string, string>;
  // Protein Lab in-lab "study tools" (all default-off so the lab is unchanged
  // until the user engages a tool):
  //  - bioInspectMode: when true, clicking an atom in the lab records it below.
  //  - bioPicks: ordered atoms picked by the inspect/measure tool. 2 → distance,
  //    3 → angle, 4 → dihedral; a pick beyond 4 restarts the chain.
  //  - bioInteractionOverlay: which interaction families to draw as 3-D dashed
  //    lines (from analyzeInteractions on the loaded structure).
  bioInspectMode: boolean;
  bioPicks: BioPick[];
  bioInteractionOverlay: BioInteractionOverlay;
  // Structure walkthrough (primary → tertiary): the residue set painted in an
  // accent color with the rest dimmed (highlightResidues), the residue the user
  // is hovering in the chat Sequence card (a marker + label in 3-D), and the
  // pending camera-framing request. All transient; cleared on structure switch.
  bioHighlight: BioHighlight | null;
  // `pinned`: set by a click on the card (the mark stays after the mouse leaves).
  bioHoverResidue: (BioResidueRef & { name: string; pinned?: boolean }) | null;
  bioFocusRequest: BioFocusRequest | null;
  bioExplode: BioExplode | null;
  // Tier-3: the second structure co-rendered + aligned onto the loaded one
  // (null = no comparison active). See BioComparison.
  bioComparison: BioComparison | null;
  // Tier-3 morph: animate/scrub the interpolation between the two compared states
  // (only meaningful while bioComparison is active). null = no morph trace shown;
  // t ∈ [0,1] is the scrub fraction; `playing` runs the A↔B oscillation.
  bioMorph: { t: number; playing: boolean } | null;
  // Partial (region-restrained) MD config: simulate a region around the seed
  // residues at `temperature` K with the boundary frozen. `name` ties it to the
  // selected structure. null = off. The heavy MD state lives in the runner
  // component (refs), not here.
  // Run-control state only (implicit solvent is CONFIG, persisted on
  // projectState.bioSimSolvent, edited in the settings popover). The integrator
  // is always Langevin (see bioPartialMD.tsx).
  bioPartialMD: {
    name: string;
    seedLabels: string[];
    temperature: number;
    running: boolean;
    seedCenter?: [number, number, number]; // site-center seed (pocket/cofactor/ligand)
    mobileRadius?: number; // region radius (Å) — set by the draggable region sphere
    includeLigand?: boolean; // P5: include the bound ligand (polar-H) as mobile in the region
  } | null;
  // The draggable/resizable region-selection sphere (pre-Start): a center + mobile
  // radius (Å) the user can move/resize on-canvas to define the partial-MD region
  // without clicking atoms. null = no sphere shown. startSim reads it; the canvas
  // component (bioRegionSphere) renders + edits it.
  bioRegionSelect: { center: [number, number, number]; radius: number } | null;
  // Region radius (Å) while the sphere is TRACKING the ligand (no explicit center
  // pinned) — kept separate so resizing the tracking sphere doesn't pin its center.
  bioRegionRadius: number | null;
  // P5: the ligand's MD-ready atoms (posed to the PDB frame, polar-H-filtered)
  // published by bioLigand.tsx so the partial-MD runner can fold the ligand into
  // the region without re-parsing. null = no ligand.
  bioLigandMdAtoms: { name: string; element: string; pos: [number, number, number]; charge?: number }[] | null;
  // P3: when true, bioLigand runs a rigid-body relax of the ligand into the
  // pocket (RigidBodyDocking), then commits the best pose and clears this.
  bioLigandDocking: boolean;
  // The pose the last Dock committed (null before any dock this session), so a
  // Ligand card can say whether the current pose is docked or only placed.
  bioLigandDockedPose: MoleculeTransform | null;
  // How the last dock ended, written by bioLigand when it clears
  // bioLigandDocking: the dockLigand tool awaits it so the agent reads the
  // committed pose, not the starting one. null while a dock is running (or
  // before any dock).
  bioLigandDockOutcome: BioLigandDockOutcome | null;
  // Bumped by a chat's "Sign in" prompt (ChatSignInPrompt); the toolbar opens
  // its Sign in menu in answer, so every sign-in goes through the one menu and
  // its terms check.
  signInMenuRequest: number;
  // Readout published by the partial-MD runner (throttled): region sizes, live
  // temperature, step count, or an error code. null = nothing to show.
  bioPartialMDStatus: { atoms: number; mobile: number; temperature: number; step: number; error?: string } | null;
  // Live time-series for the partial-MD "Analyze" energy chart — appended by the
  // runner on each readout tick (elapsed time in fs; kinetic + potential energy of
  // the mobile region; total = ke + pe). Capped ring buffer; cleared on region
  // rebuild/reset.
  bioPartialMDSeries: { t: number; ke: number; pe: number }[];
  // The partial-MD Analyze panel (live T / energy chart) visibility.
  bioSimAnalysisVisible: boolean;
  // Bumped by the control panel's Reset button; the runner re-seeds the region to
  // its initial coordinates (and zeroes the series) whenever this changes.
  bioPartialMDResetSignal: number;
  // When true, the partial-MD runner energy-minimizes the region (steepest
  // descent) instead of stepping dynamics, then clears it on convergence. Pauses
  // dynamics while it runs. Transient (not persisted).
  bioPartialMDMinimizing: boolean;
  // Pending temperature-scale factor from Heat/Cool: the runner multiplies the
  // mobile atoms' velocities by sqrt(factor) once, then clears it. This is how
  // Heat/Cool changes T under NVE (no thermostat); under NVT it's an instant
  // nudge the thermostat then holds. null = nothing pending.
  bioPartialMDVelScale: number | null;
  // Tier 2: live protein–ligand interaction score (vdW + H-bond + clash), computed
  // by bioLigand.tsx as the ligand pose changes. null = no ligand / not scored.
  bioLigandScore: {
    total: number;
    vdw: number;
    hBond: number;
    clashCount: number;
    contactCount: number;
    hBondCount: number;
  } | null;
  // Tier 2: residue-level protein–ligand interaction report (which residues H-bond
  // or clash with the ligand, the top favorable-energy contributors, and the
  // electrostatic total), computed by bioLigand.tsx alongside the score. Powers the
  // "why it binds" readout + the analyzeLigandInteractions agent tool. null = none.
  bioLigandReport: {
    hbonds: string[];
    clashes: string[];
    contributors: { res: string; energy: number }[];
    electrostatic: number;
  } | null;
  // Biomolecule analogue of hoveredMolecule / hoveredCrystalName. Set when a
  // per-structure curve/bar in the comparative analysis charts is hovered (and
  // by the gallery card on pointer enter), read by the gallery to highlight the
  // matching structure card. Keyed by structure name (matches `biomolecules`).
  hoveredBiomoleculeName: string | null;
  // Crystal analogue of hoveredMolecule. Set by the crystal gallery card
  // on pointer enter / cleared on leave, read by the graph panels to
  // surface per-axis values, fade non-hovered lines, etc. Keyed by the
  // CrystalState name so consumers can resolve back to the active crystal
  // via the `crystals` array in projectState.
  hoveredCrystalName: string | null;
  // Group of molecule names to co-highlight in the gallery while a
  // multi-molecule chart element is being hovered — pairwise dots in the
  // structure/property scatter, coincident clusters in the bubble chart,
  // etc. Distinct from hoveredMolecule so the existing single-molecule
  // highlight contract stays intact for all other callers.
  hoveredMoleculeGroup: string[] | null;
  // Set of molecule names to highlight in the gallery while a structural-
  // feature row is being hovered (e.g. a Bit Correlations row). Lets the
  // user "ground" an abstract feature in the gallery cards that exhibit
  // it without us needing to name the feature itself yet.
  bitHighlightedMoleculeNames: string[] | null;
  selectedPlane: number;
  regressionAnalysis: boolean;
  regression: PolynomialRegression | null;

  pickMode: PickMode;
  pickedAtomIndex: number;
  // Multi-selection in the chamber. `pickedAtomIndex` stays as the
  // "last-picked" anchor for single-target consumers (info panel,
  // context-menu placement); this set is the full selection used for
  // highlighting and bulk operations. Empty set === nothing selected.
  pickedAtomIndices: Set<number>;
  pickedMoleculeIndex: number;
  // Multi-selection in MOLECULE pick mode — same pattern as
  // pickedAtomIndices. pickedMoleculeIndex remains the last-picked anchor.
  pickedMoleculeIndices: Set<number>;
  // Crystal-chamber molecule selection. Indexes into the connected-
  // components map computed from the radial-bond graph in CrystalViewer
  // (one ID per cluster — a single H2O = 1 molecule, an isolated atom =
  // its own one-atom molecule). -1 means nothing selected. Separate
  // from pickedMoleculeIndex because crystal "molecules" are derived
  // on the fly from the supercell, not stored as Molecule instances.
  pickedCrystalMoleculeIndex: number;
  // Crystal-chamber atom selection. Alt+right-click (or Alt+left-click)
  // sets this and clears pickedCrystalMoleculeIndex — the two are
  // mutually exclusive selection modes. -1 means nothing selected.
  // Indexes into the expanded supercell's atoms array, same indexing
  // CrystalViewer uses.
  pickedCrystalAtomIndex: number;
  // True when the right-click that set the current crystal pick landed on a
  // periodic-image (ghost / cosmetic boundary) atom rather than a real one.
  // The pick itself still resolves to the source real atom; this flag only
  // lets the context menu note that the user clicked an image copy.
  pickedCrystalAtomIsGhost: boolean;
  // Number of render-only image atoms (periodic-image bond ghosts + cosmetic
  // boundary-completion atoms) currently drawn at the supercell boundary in
  // the crystal viewer. Published by CrystalViewer so the toolbar's atom-count
  // popover can tell the user how many of the visible spheres are ghosts (they
  // are NOT part of the simulated model). 0 when none are drawn.
  crystalGhostAtomCount: number;
  // A snapshot taken at Copy time — not a list index, which a later delete or
  // cut shifts onto another molecule (or past the end).
  copiedMolecule: Molecule | null;
  cutMolecule: Molecule | null;

  boundingSphereRadius: number;

  // Docking: the active ligand's atoms in ligand-local coordinates, flattened
  // for the docking scorer (DockingScore). Published by dockingViewer when a
  // ligand is parsed; consumed by dockingScorePanel to compute the live
  // binding score. Empty array when no ligand is loaded.
  ligandScoreAtoms: ScoreAtom[];
  // Docking: the active ligand's element symbols and bond list, for building a
  // flexible (rotatable-bond) ligand in the Stage-3/4 pose search. Published by
  // dockingViewer alongside ligandScoreAtoms; null when no ligand is loaded.
  ligandTorsionInput: { elements: string[]; bonds: TorsionBond[] } | null;
  // Docking: RMSD (Å) of the ligand's docked conformation from its input
  // conformation — how much it deformed to dock. 0 when not flex-docked.
  ligandConformationRmsd: number;
  // Docking: number of rotatable bonds in the active ligand. 0 ⇒ a rigid
  // ligand, for which the conformation-change readout and ghost overlay hide.
  ligandRotatableBondCount: number;
  // Docking: candidate binding pockets from the last "Find pockets" run, ranked
  // largest-first. Ephemeral — recomputed on demand, cleared on protein change.
  detectedPockets: Pocket[];
  // Docking: per-receptor-atom residue label (e.g. "ASP25/A"), parallel to the
  // protein atom order — lets pocket detection identify each pocket's lining
  // residues. Published by dockingViewer when a protein loads.
  proteinResidueLabels: string[];
  // Docking: pharmacophore features detected on the LIGAND (in ligand-local
  // coords, the pose transform is applied at use site) and on the RECEPTOR
  // (in receptor-atom frame). Used by the pharmacophore-guided search to add
  // an alignment bonus when complementary features (e.g. aromatic ↔ aromatic
  // for π-π stacking, positive ↔ negative for salt bridges) line up.
  ligandPharmacophoreFeatures: PharmacophoreFeature[];
  receptorPharmacophoreFeatures: PharmacophoreFeature[];
  // Docking: true while the Stage-2 rigid-body relax loop (dockingRelax) is
  // running. Drives the Relax/Stop button and tells the score panel to read
  // the live score below instead of recomputing from ligandTransform.
  relaxingLigand: boolean;
  // Docking: true while the relax loop is in its approach phase — gliding a
  // not-yet-touching ligand toward the receptor before the descent begins.
  // Lets the score panel show "Approaching…" instead of a meaningless ~0 score.
  relaxApproaching: boolean;
  // Docking: the score from the most recent relax / search step. Only
  // meaningful while relaxingLigand or searchingLigand is true — the per-frame
  // channel for the live readout (ligandTransform is persisted, so it is not
  // written every frame).
  dockingScore: DockingScoreResult | null;
  // Docking: true while the Stage-3 automated pose search (dockingSearch) is
  // running. Drives the Search/Stop button and disables Relax.
  searchingLigand: boolean;
  // Docking: progress of the running search (completed / total runs), or null
  // when no search is running.
  searchProgress: { completed: number; total: number } | null;
  // Docking: ranked, de-duplicated best poses from the last completed search.
  // Consumed by dockingScorePanel; clicking a result applies its pose.
  searchResults: SearchPose[];
  // Docking: rolling buffer of recent search poses across runs (most recent
  // first, capped at MAX_POSE_HISTORY entries). Each pose carries the stable
  // `pose_<run>_<rank>` id minted in dockingSearch.finishSearch, so an agent
  // (or any later consumer) can refer to "the second-best pose from two
  // searches ago" without depending on the live `searchResults` which gets
  // clobbered on every new search. Ephemeral — not persisted to the project.
  dockingPoseHistory: SearchPose[];
  // Docking: the LIVE ligand transform while an animated run (relax / search /
  // screening) is driving the pose imperatively. The persisted
  // `projectState.ligandTransform` is frozen during these runs (we only commit
  // at the end as an undoable), so the viewer's interaction overlay can't be
  // recomputed from it. Drivers publish here on every progress tick; the
  // viewer's interactions useMemo prefers this when set, falls back to the
  // persisted transform otherwise. Null when no run is in progress.
  liveLigandTransform: { x: number; y: number; z: number; euler: number[] } | null;
  // Docking: true while a virtual-screening run is active. The driver docks
  // each gallery molecule sequentially at the chosen binding site, recording
  // the best pose per candidate. Drives the "Screen gallery" button + the
  // disabled state of the rest of the docking actions.
  screeningActive: boolean;
  // Docking: live progress of the screening run — which candidate is being
  // docked and how many remain. Cleared back to null when the run finishes.
  screeningProgress: { completed: number; total: number; currentName: string } | null;
  // (Docking screening results now live in projectState.screeningResults so
  // they persist with the project — see types.ts. Kept out of primitive
  // store because reload should bring them back.)
  // Docking: true while a pocket-aware AI ligand-generation run is in
  // progress (dockingPocketGeneration). Drives the "Design ligands" button's
  // disabled/Stop state.
  pocketGenerating: boolean;
  // Docking: live progress of the pocket-aware generation run — how many
  // candidates have completed (and how many were requested). Null when not
  // running.
  pocketGenerationProgress: { completed: number; total: number } | null;
  // (Docking screening-analysis result lives in
  // projectState.screeningAnalysisResult — persists alongside the results
  // table itself. Interpretation is now produced in the Drug Discovery chat
  // via the docking agent's getScreeningResults tool, not an in-panel stream.)

  startSimulation: boolean;
  resetSimulation: boolean;
  // Set alongside resetSimulation when the reset is from OPENING (or newly saving)
  // a project, not a user Reset. A project-open reset must preserve the loaded
  // piston plate position (pistonSnapshot) — a plain reset clears it and snaps the
  // plate to the box top, detaching it from a saved compressed gas.
  resetKeepPiston: boolean;
  updateDataFlag: boolean;

  moleculeDataUpdated: boolean;

  // Per-card pick state for the gallery edit context menu. Keyed by molecule
  // name (more stable than gallery index, which shuffles on sort/filter). The
  // atom index is into the molecule's own atom array, matching the SDF order
  // so OpenChemLib can address the same atom by the same number.
  galleryEditPickedMoleculeName: string | null;
  galleryEditPickedAtomIndex: number;
  galleryEditMenuAnchor: { x: number; y: number } | null;
  galleryEditBusy: boolean;

  // Dialog state for the two prompt-style edit ops (Custom SMILES,
  // Add Bond). Kept on the primitive store rather than as local state
  // inside the menu component because the dropdown auto-closes on item
  // click — which unmounts the menu and would destroy the local state
  // before the modal had a chance to render. A persistent dialog
  // component subscribes to these fields and renders the modal.
  galleryEditCustomSmilesFor: { moleculeName: string; hydrogenIndex: number } | null;
  galleryEditAddBondFor: { moleculeName: string; atomA: number } | null;

  // Name of the gallery molecule whose UV-Vis (color) modal is open, or
  // null. Set by the per-card ⋮ menu's "Predict Color" item and the agent's
  // computeUVVisSpectrum tool; a persistent modal mounted at gallery level
  // subscribes and renders. (Same persistent-host pattern as the gallery
  // edit dialogs above — see UVVisSpectrumModal.)
  uvVisSpectrumForMoleculeName: string | null;
  // Which prediction engine the modal should open with (registry key, e.g.
  // 'ppp' / 'huckel'), or null to use the modal's default. The agent tool sets
  // it to whatever `method` it computed with so the modal matches the agent's
  // reported result; the card menu leaves it null (modal default).
  uvVisSpectrumEngine: string | null;

  latestAddedMoleculeName: string | null;
  setLatestAddedMoleculeName: (name: string | null) => void;

  enableRotate: boolean;
  autoRotate: boolean;

  // Active VR session in the chamber. 'webxr' = Android/Quest WebXR immersive
  // session (Three's gl.xr drives rendering); 'stereo' = iOS Safari fallback
  // that splits the canvas into two side-by-side viewports and steers the
  // camera from DeviceOrientationEvent. null = no VR. Mobile-only — the
  // launch button never mounts on desktop.
  vrMode: 'webxr' | 'stereo' | null;

  resetViewFlag: boolean;
  resetView: () => void;

  zoomViewFlag: boolean;
  zoomScale: number;
  zoomView: (scale: number) => void;

  waiting: boolean;
  setWaiting: (b: boolean) => void;

  // True from page load until a ?userid=&project= deep link has been fetched
  // (or failed). The gallery pane is not mounted meanwhile: it would build the
  // DEFAULT project type's panel — for the molecule chat, a WebGL canvas
  // costing ~0.5 s of main thread — only to tear it down when the linked
  // project of another type arrives, delaying the lab's first picture.
  deepLinkPending: boolean;

  generating: boolean;
  setGenerating: (b: boolean) => void;

  // True only during a property-prediction LLM call (e.g. polymer Tg/Tm,
  // crystal band gap). Separate from `generating` so the gallery's
  // "Generate Molecule" button doesn't swap to a spinner when the user
  // is only predicting properties — those are independent intents that
  // share the reasoning-stream panel for progress display.
  predicting: boolean;
  setPredicting: (b: boolean) => void;

  // Caller-supplied title for the reasoning-stream panel (e.g. "Predicting
  // properties", "Generating monomer", "Analyzing results"). Falls back to
  // the generic "Model reasoning" / "Agent activity" labels when null.
  reasoningStreamTitle: string | null;
  setReasoningStreamTitle: (title: string | null) => void;

  // The user's prompt for the turn that is CURRENTLY streaming. Shown in the
  // reasoning panel so the user keeps seeing what they asked while the AI reasons
  // and answers (the durable chat message is only stamped when the turn ends).
  // Set at the start of each turn; the panel that reads it is mounted only while
  // generating, so it never lingers.
  reasoningPrompt: string | null;
  setReasoningPrompt: (prompt: string | null) => void;

  // True only while the multi-round agent loop is running (NOT for plain
  // non-agent generations). Drives the reasoning panel title between
  // "Model reasoning" and "Agent activity".
  agentRunning: boolean;
  setAgentRunning: (b: boolean) => void;

  // True when the immediately preceding gallery-chat turn was a docking-agent
  // run (Drug Discovery projects). Lets a keyword-less follow-up like "apply"
  // or "yes" continue the docking conversation instead of falling through to
  // the molecule generator. Ephemeral: resets on reload, which is fine — a
  // bare follow-up is only meaningful right after its docking turn.
  lastChatTurnWasDocking: boolean;

  // Live transcript of the model's reasoning while a generation is streaming.
  // Cleared between generations. Only populated for providers that emit a
  // reasoning channel (currently DeepSeek) and only when showReasoning is on.
  reasoningStream: string;
  appendReasoningStream: (chunk: string) => void;
  clearReasoningStream: () => void;
  showReasoning: boolean;
  setShowReasoning: (b: boolean) => void;
  reasoningStartTime: number | null;
  reasoningElapsedMs: number | null;
  startReasoningTimer: () => void;
  stopReasoningTimer: () => number; // returns final elapsed ms

  // Ephemeral, NON-persisted chat entries produced by system slash-commands
  // (e.g. "/help"). Rendered inline at the bottom of the chat thread, but held
  // here instead of on projectState.chatMessages so command output is never
  // saved to the project, folded into the rolling chat summary, or fed back to
  // the interpreter / agent as conversation. Cleared by "/clear", the per-item
  // dismiss button, and a project switch.
  ephemeralChatMessages: ChatMessageInterface[];
  addEphemeralChatMessage: (message: ChatMessageInterface) => void;
  removeEphemeralChatMessage: (id: string) => void;
  clearEphemeralChatMessages: () => void;

  // These stores the settings from newProjectDialog.tsx, because we don't want to overwrite
  // the local state in the common store yet. Don't be confused with commonStore's projectState,
  // which holds the state of a project.
  projectType: ProjectType;
  projectTitle: string | null;
  projectDescription: string | null;

  createProjectFlag: boolean;
  createProjectDialog: boolean;
  setCreateProjectDialog: (b: boolean) => void;
  saveProjectFlag: boolean;
  setSaveProjectFlag: (b: boolean) => void;
  saveProjectAsFlag: boolean;
  saveProjectAsDialog: boolean;
  setSaveProjectAsDialog: (b: boolean) => void;
  saveAndThenOpenProjectFlag: boolean;
  // Fired by the File-menu "Recent Projects" items to open a specific recent
  // project directly (no save first). CloudManager reacts (it owns
  // loadProject) and reads the target from commonStore.recentProjectToOpen.
  openRecentProjectFlag: boolean;

  showSupercellSizeModal: boolean;
  setShowSupercellSizeModal: (b: boolean) => void;
  showGenerateCrystalModal: boolean;
  setShowGenerateCrystalModal: (b: boolean) => void;
  // Polymer Builder "Build Chain" dialog. Flipped by the toolbar button
  // (toolBarButtons.tsx) once the user has defined a monomer; the modal
  // (buildChainModal.tsx) is mounted polymer-only in projectGallery.tsx.
  showBuildChainModal: boolean;
  setShowBuildChainModal: (b: boolean) => void;
  // The gallery card whose Coarse-grain dialog (coarseGrainModal.tsx) is open,
  // or null. Set by the card's button and the /coarse-grain slash command.
  coarseGrainMoleculeName: string | null;
  setCoarseGrainMoleculeName: (name: string | null) => void;
  // Polymer Builder "Generate Monomer" dialog. Flipped from the gallery
  // description panel's AI button when the project is a polymer project;
  // owns the SMILES/prompt → SDF generation plus head/tail atom picker,
  // and writes polymerMonomerSdf et al onto the project state on OK.
  showMonomerInputModal: boolean;
  setShowMonomerInputModal: (b: boolean) => void;
  // Set by the crystal chat's submit handler when the user fires
  // generation directly from chat (rather than via the AI button that
  // opens the full options dialog). When the modal sees this flag on
  // mount, it auto-runs onOk() with the current store-driven defaults
  // (provider, agent mode, etc.) and stays hidden — so the chat gets
  // the same "generate directly" UX the molecule chat has, without
  // duplicating the generation pipeline.
  pendingCrystalChatSubmit: boolean;
  setPendingCrystalChatSubmit: (b: boolean) => void;

  showPeriodicTable: boolean;

  showProjectsFlag: boolean;
  updateProjectsFlag: boolean;
  showProjectListPanel: boolean;
  showExamplesDrawer: boolean;

  // When true, the MD integrators push one J(t) = Σᵢ qᵢ vᵢ(t) sample per
  // step into useDataStore.dipoleCurrentBuffer, feeding the IR-spectrum
  // panel. Off by default so unused MD runs don't pay even the trivial
  // per-step accumulator cost (a charge-weighted velocity sum over atoms).
  recordDipoleCurrent: boolean;
  // Monotonic counters bumped by the agent's analysis tools to ask the
  // IR-spectrum / g(r) panels to run their own Compute (so the chart in the
  // lab actually renders the result — the agent computes in its own layer for
  // interpretation, but only the panel's handler populates its chart).
  computeIrSpectrumFlag: number;
  computeGOfRFlag: number;
  // Same drive-the-panel's-own-Compute pattern as computeIrSpectrumFlag, for
  // the MSD/diffusion panel and the phonon/vibrational-DOS panel. Bumped by the
  // agent's computeMSD / computeVibrationalDOS tools so the curve renders in
  // the lab, not just the numbers returned to the agent.
  computeMsdFlag: number;
  computeVdosFlag: number;
  // Agent-authored peak labels drawn on the IR-spectrum chart (see
  // SpectrumAnnotation). Written by the annotateSpectrum agent tool, read by
  // irSpectrumPanel. Ephemeral (not persisted) and cleared whenever the
  // spectrum is recomputed / cleared / the simulation is reset, since stale
  // labels would no longer line up with a fresh curve. null = none drawn.
  spectrumAnnotations: SpectrumAnnotation[] | null;
  // Agent-authored markers on the g(r) panel (x = r in Å) and the energy graph
  // (x = time in fs). Same lifecycle as spectrumAnnotations: written by the
  // annotate* agent tools, read by the panels, ephemeral, cleared when the
  // underlying data is cleared / the simulation is reset. null = none drawn.
  gOfRAnnotations: GraphAnnotation[] | null;
  energyAnnotations: GraphAnnotation[] | null;
  // More per-panel agent-marker slots: speed histogram (x = speed in m/s),
  // MSD/diffusion (x = lag time in ps), vibrational DOS (x = wavenumber cm⁻¹).
  // Same lifecycle as the others.
  speedAnnotations: GraphAnnotation[] | null;
  msdAnnotations: GraphAnnotation[] | null;
  dosAnnotations: GraphAnnotation[] | null;
  // Bumped by the crystal agent's openBandStructureViewer tool; watched by
  // crystalSimulationControls to open the band-structure modal.
  openBandStructureFlag: number;
  // Dynamics-lab Web-Worker coordination (simulationControls watches these).
  // engineRebuildFlag: bumped by dynamicsViewer after a fresh MolecularDynamics
  // mirror is built → controls (re)init the worker engine from it.
  // syncEngineFlag: bumped by any edit to the mirror's atoms (gizmo / context
  // menu / agent tool) → controls post a syncState to the worker.
  // pendingTempChangePercent: a heat/cool request that must reach the worker
  // engine (set by the agent's controlSimulation; consumed by the controls).
  engineRebuildFlag: number;
  syncEngineFlag: number;
  // Bumped whenever a project is (re)loaded (setProjectState). The dynamics
  // viewer keys its scene-rebuild effect on this in ADDITION to the structural
  // signature, so RELOADING THE SAME PROJECT (identical molecule names + SDF,
  // hence an unchanged signature) still tears down and rebuilds the render
  // molecules + mirror engine from the freshly-loaded atoms. Without it, an
  // in-app reload leaves the viewer/mirror holding the pre-reload atoms, so the
  // energy minimizer (and anything else reading the mirror) sees stale state.
  sceneReloadFlag: number;
  pendingTempChangePercent: number | null;
  // pendingPistonPlate: a manual plate placement (engineSync.setPistonPlateHeight
  // — the ⚙ Piston Position field / Top button / piston switch, the agent's
  // configureSimulation pistonHeight, the gas-law sweeps' plate reset) that must
  // reach the worker engine. Consumed by simulationControls, which posts it as a
  // `setPistonPlate` message applied between chunks — NOT a rebuild, which would
  // zero the clock and wipe every series and tape mid-run.
  pendingPistonPlate: { position: number; velocity: number } | null;
  // True while the molecular-dynamics Web Worker is alive and driving the
  // engine. vrMode reads it to skip main-thread stepping (the worker keeps the
  // mirror synced); false ⇒ fallback, so VR steps the mirror itself.
  mdWorkerActive: boolean;
  // True from the moment the controls post `pause` to the MD worker until its
  // `paused` packet has leveled the mirror with it. In that window the mirror is
  // one chunk BEHIND the worker and about to be overwritten, so an edit made then
  // is lost. startSimulation flips false at once, before this clears — agent
  // tools that need a paused lab (spark / minimizeLab / fitBoxToLab) await
  // `awaitPauseSettled` in src/agent/tools.ts instead of trusting that flag.
  mdPauseSettling: boolean;
  // Bumped by simulationControls each time a Reset or a heat/cool has LANDED in
  // the mirror (the worker's `reset` / `tempChanged` reply applied, or the
  // no-worker fallback done). `controlSimulation` reads it before raising the
  // request and awaits a bump (engineSync.awaitControlReply) before taking its
  // `state` snapshot, so the block really is post-action — without this the
  // snapshot showed the pre-reset temperature / plate and the pre-heat setpoint.
  mdControlReplySeq: number;
  // Same, for the crystal-dynamics Web Worker (crystalSimulationControls).
  crystalWorkerActive: boolean;
  // Agent "delta channel" (src/agent/sceneSnapshot.ts). lastAgentSnapshot is a
  // coarse fingerprint stamped at the END of each agent run; the next run diffs
  // against it to learn what the user did between turns. pendingSceneDelta is
  // that diff, frozen at the start of the current run for getRecentChanges to
  // read. Deliberately NOT persisted: the agent conversation itself isn't
  // restored across reload, so a baseline from a prior session has no "last
  // reply" to anchor to — ephemeral (reset on project load) is correct.
  lastAgentSnapshot: SceneSnapshot | null;
  pendingSceneDelta: SceneDelta | null;
  // Set by the agent's loadMoleculeToLab tool to the name of a gallery molecule
  // to place into the simulation chamber. lab.tsx (which owns the placement
  // logic + the parsed-structure cache) consumes it on the next render and
  // clears it back to null.
  pendingChamberMoleculeName: string | null;
  // How many copies of pendingChamberMoleculeName to place (default 1). Lets the
  // agent drop e.g. 10 water molecules in one tool call instead of one per round.
  pendingChamberMoleculeCount: number;
  // Which part of the container to drop the copies into, as a list of WORLD-axis
  // tokens (bottom=-Z, top=+Z, left=-X, right=+X, back=-Y, front=+Y) whose
  // intersection is the target region: one token = a face, two = an edge, three =
  // a corner (e.g. ['top','left'] = upper-left). null / [] ≡ center of the box
  // (the historical behavior). The tool resolves any screen-frame request to
  // these world tokens. Consumed + cleared alongside the name.
  pendingChamberMoleculeRegion: string[] | null;
  // How deep into the box (0<f≤1) each named region axis reaches from its face:
  // 0.5 = a half (the default / historical behavior), 0.25 = a quarter, etc. Lets
  // "the lower quarter" be a narrower band than "the lower half". null → 0.5.
  pendingChamberMoleculeRegionFraction: number | null;
  // Explicit Cartesian drop point [x,y,z] in Å for the pending molecule(s). When
  // set it overrides the region: the first copy lands exactly here, extra copies
  // spiral out from it. null → use region / center. Consumed + cleared with name.
  pendingChamberMoleculePoint: number[] | null;
  // When true, scatter the pending copies UNIFORMLY across the whole container
  // (random points, overlap-rejected) instead of clustering them compactly around
  // an anchor — used to seed a gas that fills the box. Consumed + cleared with name.
  pendingChamberMoleculeScatter: boolean;
  // Sample-stride for the dipole-current accumulator, in integrator
  // steps. Sampling every Nth step keeps the buffer covering a useful
  // trajectory length without oversampling — at a 0.5-fs timestep, N=4
  // gives a 2-fs sample stride (Nyquist ≈ 8 340 cm⁻¹, covering all IR).
  // NOTE: the Molecular Modeling lab no longer reads these three fields —
  // its stride is derived from the live timestep (sampleStride.ts) so the
  // Nyquist limit doesn't drift with dt. The crystal lab still uses them.
  dipoleCurrentSampleEveryNSteps: number;

  // When true, the MD integrators push per-tracked-atom velocities into
  // useDataStore.velocityBuffer, feeding the phonon-DOS panel. Off by
  // default. The tracked-atom set is locked in by the DOS panel via
  // velocityBuffer.prepare(atoms) the first time the user starts
  // recording, so this flag only controls *whether* to capture; the
  // *which atoms* decision lives in the buffer.
  recordVelocity: boolean;
  // Sample-stride for the velocity accumulator, in integrator steps.
  // Same default rationale as dipoleCurrentSampleEveryNSteps — N=4 at
  // a 0.5 fs timestep gives a 2 fs sample stride.
  velocitySampleEveryNSteps: number;
  // When true, integrators push per-tracked-atom unwrapped positions
  // into useDataStore.positionBuffer for the MSD / diffusion panel.
  // Same lazy-prepare pattern as recordVelocity.
  recordPosition: boolean;
  // Sample-stride for the position accumulator, in integrator steps.
  // MSD doesn't need the high temporal resolution that IR / VDOS do
  // (no Nyquist concern — long-time displacement is what matters), so
  // a larger default stride could be used, but for consistency we
  // keep the same N=4 default.
  positionSampleEveryNSteps: number;

  // "Measurement" analysis tab: while true, the MD loop samples the geometric
  // measurement (distance / angle / dihedral) of every track in
  // projectState.measurementTracks into useDataStore.measurementTimeSeries each
  // collectInterval. The track DEFINITIONS live on projectState (persisted with
  // the project); only this recording flag is ephemeral. Reset on reload.
  recordMeasurement: boolean;

  // Active agent scientific-inquiry experiment (hypothesis → run → measure →
  // confirm/refute). Set by the recordHypothesis agent tool, completed by
  // reportFinding. Ephemeral (not persisted); one at a time. Null when none is
  // in flight.
  agentInquiry: AgentInquiry | null;
  // Active agent engineering-design challenge (define → build → test → evaluate →
  // iterate). Set by recordDesignGoal, completed by reportDesignOutcome. Same
  // ephemeral, one-at-a-time semantics as agentInquiry.
  agentDesign: AgentDesign | null;
  // Visual artifacts (charts) a tool produced DURING the current agent run, to
  // be snapshotted onto this turn's chat message (the "chat as lab report"
  // flow). Populated by tools like computeUVVisSpectrum, cleared before each run
  // and captured after. Ephemeral; the durable copy lives on the chat message.
  agentChatAttachments: ChatAttachment[];
  // Constant-power heating for the crystal lab: eV added to the whole system's
  // kinetic energy every step while the heat bath is OFF (see
  // CrystalDynamics.heatPerStepEv). Rides along on each worker step message.
  // Set only by the crystal agent's runHeatingCurve for the duration of its run
  // and reset to 0 after; ephemeral by design — a saved project never heats
  // itself on reopen.
  crystalHeatPerStepEv: number;
  // Live pin/release request for the crystal lab's atoms (1 = fixed), indexed
  // like the engine's atom list. Set by the crystal agent's runSeededGrowth to
  // hold the crystal seed while the rest is melted and regrown;
  // crystalSimulationControls forwards it to the worker (`setFixed`) and the
  // mirror. `seq` makes a repeated identical mask still fire. Ephemeral.
  crystalFixedRequest: { mask: number[]; seq: number } | null;
  // Sparse per-atom removed / fixed / restraint / velocity edit for the crystal
  // lab's WORKER engine (see crystal/crystalAtomStateRequest.ts). Set by the
  // context menu's Cut / Restrain after they edit the mirror in place;
  // crystalSimulationControls forwards it as `setAtomState`. Ephemeral.
  crystalAtomStateRequest: CrystalAtomStateRequest | null;
  // "Push the mirror's full frame (positions, baseline, velocities, step) to
  // the worker now" — set by a kept-state / final-frame restore (see
  // crystal/crystalAtomStateRequest.ts requestCrystalFrameSync). Ephemeral.
  crystalFrameSyncRequest: { seq: number } | null;
  // Heat / cool the crystal lab by `percent` the way its Heat / Cool buttons
  // do — set by the crystal agent's controlCrystalSimulation, which used to
  // rescale only the mirror (overwritten by the next worker packet, never
  // reaching the worker). crystalSimulationControls applies it and bumps
  // crystalTemperatureReplySeq once the engine has the new velocities.
  crystalTemperatureRequest: { percent: number; seq: number } | null;
  crystalTemperatureReplySeq: number;

  // One-shot signals for the share / remove-from-commons actions on the
  // currently open project. Consumed by useFlag in cloudManager.
  shareProjectFlag: boolean;
  removeFromCommonsFlag: boolean;

  userCount: number;

  // Set by the Project menu's "Submit Project to Teacher" item; consumed by the
  // SubmitToClassModal mounted in appCreator.
  showSubmitToClassModal: boolean;

  // Set by the Project menu's "Export 3D Model (STL)" item; consumed by the
  // ExportModelModal mounted in appCreator.
  showExportModelModal: boolean;

  // "Export as displayed" request: set by ExportModelModal, consumed by the
  // active viewer (dynamicsViewer), which exports its own live rendered group
  // — avoids any ref-publishing timing issues. Cleared by the viewer once read.
  exportRenderedRequest: { format: string; scale: number; fileName: string; fitToBedMm?: number } | null;

  // Live progress of a 3D-print export conversion (building/merging geometry +
  // encoding the file). Set by exportRenderedScene via its progress callback and
  // rendered by ExportProgressOverlay; null when no export is running. `percent`
  // is 0..100; `phase` is an i18n-key suffix under `exportModel`.
  exportProgress: { phase: string; percent: number } | null;

  // Stash of acceptance info captured before the user authenticates. Set by
  // the pre-sign-in gate when the user clicks Accept; consumed by
  // registerUser to populate acceptedTermsAt / acceptedTermsVersion on the
  // user's Firestore record. Cleared after registerUser finishes.
  pendingAcceptance: { acceptedTermsAt: string; acceptedTermsVersion: string } | null;

  // Raised once right after an anonymous sign-in to offer the new account a
  // screen name (see src/screenName.ts). Skippable, and never raised again on
  // its own — the avatar menu and the account page are the way back to it.
  askForScreenName: boolean;

  currentTemperature: number;
  currentPressure: number;
  currentDensity: number;
  // Species census captured the moment a reactive run starts: {formula: count}
  // of the initial mixture. The dynamics HUD diffs the live tally against this
  // to show which species are being consumed (reactants) vs produced
  // (products). Null when no reactive baseline has been captured (cleared on
  // reset); recaptured on the next play.
  reactiveSpeciesBaseline: Record<string, number> | null;
  // Smoothed wall-clock cost of one crystal-MD step (ms). The chamber
  // HUD turns this into a simulation-throughput readout.
  crystalMsPerStep: number;

  showThrustFlame: boolean;
  navCoordinates: number[];

  // Landing page overlay (4 project-type tiles + Commons/Tutorial). Shown on
  // every fresh load until the user picks a tile or dismisses; not persisted,
  // so refreshing the tab brings it back. Hidden whenever the page is opened
  // with a project deep-link (see appCreator.tsx) so curated shares skip it.
  showLanding: boolean;

  // Lesson Plan creator modal (mounted in appCreator). Opened from the Project
  // menu; generates an AI lesson plan FROM the currently open project (its
  // type/molecules/properties become the basis) and opens an editor. Ephemeral.
  showLessonPlanModal: boolean;
  // When set, the lesson-plan modal opens directly in editor mode with this
  // saved record loaded (used by the "My Lesson Plans" drawer). Cleared when
  // the modal reads it. Null → modal opens in create-from-project mode.
  lessonPlanToOpen: LessonPlanRecord | null;
  // Right-side drawer listing the user's saved lesson plans.
  showLessonPlanListPanel: boolean;
  // When set, the "submit lesson plan to a class" modal is mounted for this
  // saved plan (id + title). Null → modal closed.

  set: (fn: (state: PrimitiveStoreState) => void) => void;
  setPrimitiveStore: <K extends keyof PrimitiveStoreState, V extends PrimitiveStoreState[K]>(key: K, val: V) => void;
}

export const usePrimitiveStore = createWithEqualityFn<PrimitiveStoreState>()((set, get) => {
  const immerSet: PrimitiveStoreState['set'] = (fn) => set(produce(fn));

  return {
    set: (fn) => {
      try {
        immerSet(fn);
      } catch (e) {
        console.error('primitive store set failed', e);
      }
    },

    setPrimitiveStore(key, val) {
      immerSet((state) => {
        if (state[key] !== undefined) {
          state[key] = val;
        } else {
          console.error(`key ${key} is not defined in PrimitiveStoreState`);
        }
      });
    },

    latestVersion: undefined,
    minimizeLabFlag: false,
    minimizing: false,
    minimizingEnergy: null,
    cancelMinimizeFlag: false,
    changed: false,
    setChanged(b) {
      // A view-only (embedded/shared) project can never be saved, so it must
      // never appear dirty. Ignore attempts to mark it changed in that mode —
      // viewing, navigating, or running the simulation shouldn't show a "*".
      if (b && typeof window !== 'undefined') {
        try {
          if (new URLSearchParams(window.location.search).get('viewonly') === 'true') return;
        } catch {
          // ignore malformed/absent location and fall through
        }
      }
      immerSet((state: PrimitiveStoreState) => {
        state.changed = b;
      });
    },
    skipChange: true,
    setSkipChange(b) {
      immerSet((state: PrimitiveStoreState) => {
        state.skipChange = b;
      });
    },
    // Initialized from localStorage on desktop so a previously-muted user
    // doesn't have to re-mute every session. Mobile always reads false —
    // the toast carries the only easy undo affordance there.
    muteUndoMessage: readPersistedMuteUndoMessage(),

    updateViewerFlag: false,
    updateViewer() {
      immerSet((state: PrimitiveStoreState) => {
        state.updateViewerFlag = !state.updateViewerFlag;
      });
    },
    updateInfoFlag: false,
    updateInfo() {
      immerSet((state: PrimitiveStoreState) => {
        state.updateInfoFlag = !state.updateInfoFlag;
      });
    },

    message: undefined,

    dragAndDropMolecule: false,
    hoveredMolecule: null,
    bioModeAnimation: null,
    bioMobilityColoring: null,
    bioConservation: {},
    bioThumbnails: {},
    bioInspectMode: false,
    bioPicks: [],
    bioInteractionOverlay: { disulfide: false, saltbridge: false, hbond: false },
    bioHighlight: null,
    bioHoverResidue: null,
    bioFocusRequest: null,
    bioExplode: null,
    bioComparison: null,
    bioMorph: null,
    bioPartialMD: null,
    bioPartialMDStatus: null,
    bioPartialMDSeries: [],
    bioSimAnalysisVisible: false,
    bioPartialMDResetSignal: 0,
    bioPartialMDMinimizing: false,
    bioPartialMDVelScale: null,
    bioLigandScore: null,
    bioLigandReport: null,
    bioLigandMdAtoms: null,
    bioLigandDocking: false,
    bioLigandDockedPose: null,
    bioLigandDockOutcome: null,
    signInMenuRequest: 0,
    bioRegionSelect: null,
    bioRegionRadius: null,
    hoveredCrystalName: null,
    hoveredBiomoleculeName: null,
    hoveredMoleculeGroup: null,
    bitHighlightedMoleculeNames: null,
    selectedPlane: -1,
    regressionAnalysis: false,
    regression: null,

    pickMode: PickMode.MOLECULE,
    pickedAtomIndex: -1,
    pickedAtomIndices: new Set<number>(),
    pickedMoleculeIndex: -1,
    pickedMoleculeIndices: new Set<number>(),
    pickedCrystalMoleculeIndex: -1,
    pickedCrystalAtomIndex: -1,
    pickedCrystalAtomIsGhost: false,
    crystalGhostAtomCount: 0,
    copiedMolecule: null,
    cutMolecule: null,

    boundingSphereRadius: 10,

    ligandScoreAtoms: [],
    ligandTorsionInput: null,
    ligandConformationRmsd: 0,
    ligandRotatableBondCount: 0,
    detectedPockets: [],
    proteinResidueLabels: [],
    ligandPharmacophoreFeatures: [],
    receptorPharmacophoreFeatures: [],
    relaxingLigand: false,
    relaxApproaching: false,
    dockingScore: null,
    searchingLigand: false,
    searchProgress: null,
    searchResults: [],
    dockingPoseHistory: [],
    liveLigandTransform: null,
    screeningActive: false,
    screeningProgress: null,
    pocketGenerating: false,
    pocketGenerationProgress: null,

    startSimulation: false,
    resetSimulation: false,
    resetKeepPiston: false,
    updateDataFlag: false,

    moleculeDataUpdated: false,

    galleryEditPickedMoleculeName: null,
    galleryEditPickedAtomIndex: -1,
    galleryEditMenuAnchor: null,
    galleryEditBusy: false,
    galleryEditCustomSmilesFor: null,
    galleryEditAddBondFor: null,
    uvVisSpectrumForMoleculeName: null,
    uvVisSpectrumEngine: null,

    latestAddedMoleculeName: null,
    setLatestAddedMoleculeName(name) {
      immerSet((state: PrimitiveStoreState) => {
        state.latestAddedMoleculeName = name;
      });
    },

    // Default true (fresh visitor → show landing) UNLESS:
    //   - a persisted project is being restored — we don't want to interrupt
    //     a returning user with the picker, OR
    //   - the URL carries ?template=<id> — the user picked a specific
    //     starting template on the case-studies page and is being routed
    //     straight into that chamber.
    // Both checks are synchronous so the very first render has the right
    // value (an effect-based suppress would flash the landing for one
    // frame). The Help → Welcome menu item can still set this back to true.
    showLanding: hasRestoredProject() === false && hasTemplateInUrl() === false,
    showLessonPlanModal: false,
    lessonPlanToOpen: null,
    showLessonPlanListPanel: false,

    enableRotate: true,
    autoRotate: false,

    vrMode: null,

    resetViewFlag: true,
    resetView() {
      immerSet((state) => {
        state.resetViewFlag = !state.resetViewFlag;
      });
    },

    zoomViewFlag: true,
    zoomScale: 1,
    zoomView(scale) {
      immerSet((state) => {
        state.zoomViewFlag = !state.zoomViewFlag;
        state.zoomScale = scale;
      });
    },

    waiting: false,
    setWaiting(b: boolean) {
      immerSet((state) => {
        state.waiting = b;
      });
    },

    deepLinkPending: (() => {
      try {
        const p = new URLSearchParams(window.location.search);
        return !!p.get('userid') && !!p.get('project');
      } catch {
        return false;
      }
    })(),

    generating: false,
    setGenerating(b: boolean) {
      immerSet((state) => {
        state.generating = b;
      });
    },

    predicting: false,
    setPredicting(b: boolean) {
      immerSet((state) => {
        state.predicting = b;
      });
    },

    reasoningStreamTitle: null as string | null,
    setReasoningStreamTitle(title: string | null) {
      immerSet((state) => {
        state.reasoningStreamTitle = title;
      });
    },

    reasoningPrompt: null as string | null,
    setReasoningPrompt(prompt: string | null) {
      immerSet((state) => {
        state.reasoningPrompt = prompt;
      });
    },

    agentRunning: false,
    setAgentRunning(b: boolean) {
      immerSet((state) => {
        state.agentRunning = b;
      });
    },

    lastChatTurnWasDocking: false,

    reasoningStream: '',
    // Stream chunks land in this module-level buffer and are flushed to the
    // store at most once per animation frame. Without coalescing, providers
    // that emit large reasoning bursts (DeepSeek's reasoning_content can be
    // 100KB+ within seconds) trigger one immer mutation + one React re-render
    // + one forced sync layout (the auto-scroll useEffect reads scrollHeight)
    // per chunk — the cost grows with stream length and eventually starves
    // the main thread, freezing the entire UI. Capping at rAF-rate (~60 Hz)
    // keeps the cost bounded regardless of chunk arrival rate.
    appendReasoningStream(chunk: string) {
      reasoningStreamPending += chunk;
      if (!reasoningStreamFlushScheduled && typeof requestAnimationFrame !== 'undefined') {
        reasoningStreamFlushScheduled = true;
        requestAnimationFrame(() => {
          reasoningStreamFlushScheduled = false;
          const buffered = reasoningStreamPending;
          reasoningStreamPending = '';
          if (!buffered) return;
          immerSet((state) => {
            state.reasoningStream += buffered;
          });
        });
      }
    },
    clearReasoningStream() {
      reasoningStreamPending = '';
      reasoningStreamFlushScheduled = false;
      immerSet((state) => {
        state.reasoningStream = '';
        state.reasoningStartTime = null;
        state.reasoningElapsedMs = null;
        // Reset the echoed prompt so a hook that doesn't set one never shows a
        // stale prompt from a previous turn; hooks set it again right after.
        state.reasoningPrompt = null;
      });
    },
    showReasoning: false,
    setShowReasoning(b: boolean) {
      immerSet((state) => {
        state.showReasoning = b;
      });
    },
    ephemeralChatMessages: [],
    addEphemeralChatMessage(message: ChatMessageInterface) {
      immerSet((state) => {
        state.ephemeralChatMessages.push(message);
      });
    },
    removeEphemeralChatMessage(id: string) {
      immerSet((state) => {
        state.ephemeralChatMessages = state.ephemeralChatMessages.filter((m) => m.id !== id);
      });
    },
    clearEphemeralChatMessages() {
      immerSet((state) => {
        if (state.ephemeralChatMessages.length > 0) state.ephemeralChatMessages = [];
      });
    },
    reasoningStartTime: null,
    reasoningElapsedMs: null,
    startReasoningTimer() {
      immerSet((state) => {
        state.reasoningStartTime = Date.now();
        state.reasoningElapsedMs = null;
      });
    },
    stopReasoningTimer() {
      const start = get().reasoningStartTime;
      const elapsed = start === null ? 0 : Date.now() - start;
      immerSet((state) => {
        state.reasoningElapsedMs = elapsed;
      });
      return elapsed;
    },

    projectType: ProjectType.DRUG_DISCOVERY,
    projectTitle: null,
    projectDescription: null,

    createProjectFlag: false,
    createProjectDialog: false,
    setCreateProjectDialog(b) {
      immerSet((state) => {
        state.createProjectDialog = b;
      });
    },
    saveProjectFlag: false,
    setSaveProjectFlag(b) {
      immerSet((state) => {
        state.saveProjectFlag = b;
      });
      const mdRef = useRefStore.getState().molecularDynamicsRef;
      if (mdRef?.current) {
        const md = mdRef.current;
        for (const a of md.atoms) {
          a.initialPosition?.copy(a.position);
          a.initialVelocity?.copy(a.velocity);
        }
        // The saved state is the new Reset target on the worker engine too
        // (the controls post a pinned syncState on this flag).
        immerSet((state) => {
          state.syncEngineFlag = (state.syncEngineFlag ?? 0) + 1;
        });
      }
    },
    saveProjectAsFlag: false,
    saveProjectAsDialog: false,
    setSaveProjectAsDialog(b) {
      immerSet((state) => {
        state.saveProjectAsDialog = b;
      });
    },

    showSupercellSizeModal: false,
    setShowSupercellSizeModal(b) {
      immerSet((state) => {
        state.showSupercellSizeModal = b;
      });
    },

    showGenerateCrystalModal: false,
    setShowGenerateCrystalModal(b) {
      immerSet((state) => {
        state.showGenerateCrystalModal = b;
      });
    },
    showBuildChainModal: false,
    setShowBuildChainModal(b) {
      immerSet((state) => {
        state.showBuildChainModal = b;
      });
    },
    coarseGrainMoleculeName: null,
    setCoarseGrainMoleculeName(name) {
      immerSet((state) => {
        state.coarseGrainMoleculeName = name;
      });
    },
    showMonomerInputModal: false,
    setShowMonomerInputModal(b) {
      immerSet((state) => {
        state.showMonomerInputModal = b;
      });
    },
    pendingCrystalChatSubmit: false,
    setPendingCrystalChatSubmit(b) {
      immerSet((state) => {
        state.pendingCrystalChatSubmit = b;
      });
    },

    showPeriodicTable: false,

    showProjectsFlag: false,
    updateProjectsFlag: false,
    showProjectListPanel: false,
    showExamplesDrawer: false,
    recordDipoleCurrent: false,
    computeIrSpectrumFlag: 0,
    computeGOfRFlag: 0,
    computeMsdFlag: 0,
    computeVdosFlag: 0,
    spectrumAnnotations: null,
    gOfRAnnotations: null,
    energyAnnotations: null,
    speedAnnotations: null,
    msdAnnotations: null,
    dosAnnotations: null,
    openBandStructureFlag: 0,
    engineRebuildFlag: 0,
    syncEngineFlag: 0,
    sceneReloadFlag: 0,
    pendingTempChangePercent: null,
    pendingPistonPlate: null,
    mdWorkerActive: false,
    mdPauseSettling: false,
    mdControlReplySeq: 0,
    crystalWorkerActive: false,
    lastAgentSnapshot: null,
    pendingSceneDelta: null,
    pendingChamberMoleculeName: null,
    pendingChamberMoleculeCount: 1,
    pendingChamberMoleculeRegion: null,
    pendingChamberMoleculeRegionFraction: null,
    pendingChamberMoleculePoint: null,
    pendingChamberMoleculeScatter: false,
    dipoleCurrentSampleEveryNSteps: 4,
    recordVelocity: false,
    velocitySampleEveryNSteps: 4,
    recordPosition: false,
    positionSampleEveryNSteps: 4,
    recordMeasurement: false,
    agentInquiry: null,
    agentDesign: null,
    agentChatAttachments: [],
    crystalHeatPerStepEv: 0,
    crystalFixedRequest: null,
    crystalAtomStateRequest: null,
    crystalFrameSyncRequest: null,
    crystalTemperatureRequest: null,
    crystalTemperatureReplySeq: 0,
    shareProjectFlag: false,
    removeFromCommonsFlag: false,
    saveAndThenOpenProjectFlag: false,
    openRecentProjectFlag: false,

    userCount: 0,

    showSubmitToClassModal: false,

    showExportModelModal: false,

    exportRenderedRequest: null,

    exportProgress: null,

    pendingAcceptance: null,
    askForScreenName: false,

    currentTemperature: 300,
    currentPressure: 0,
    currentDensity: 0,
    reactiveSpeciesBaseline: null,
    crystalMsPerStep: 0,

    showThrustFlame: false,
    navCoordinates: [0, 0, 0],
  };
});
